import {
  List,
  ActionIcon,
  Flex,
  Divider,
  Text,
  Avatar,
  useMantineTheme,
  Badge,
} from "@mantine/core";
import { useContext } from "react";
import { IconPlaylistAdd } from "@tabler/icons";
import ActionCard from "src/components/ActionCard";
import NameInputForm, { InputFormElement } from "src/components/NameInputForm";
import { useModals, closeModal } from "@mantine/modals";
import { object, string } from "yup";
import { AppContext } from "src/api/AppContext";
import { ClientOneQuery } from "./overview";
import { ArrayElement } from "src/utils/helperTypes";
import {
  useCreateNotesMutation,
  useDeleteNotesMutation,
  useUpdateNotesMutation,
} from "src/generated/graphql";
import { ISOformatedDateTime } from "src/utils/dateHelper";
import {
  CardRadius,
  EMPLOYEE_ROLE_OBJ,
  HeightCard,
  ModalId,
} from "src/utils/constants";
import useWritePermission from "src/hooks/usePermission";
import { generateInitials } from "src/utils/utils";
import useTransitionMenu from "src/hooks/useTransitionMenu";

interface NotesListProps {
  notes: NonNullable<ClientOneQuery>["notes"];
  onRefetch: () => void;
  clientId: string | undefined;
  roleType: EMPLOYEE_ROLE_OBJ;
}
type NoteElement = ArrayElement<NonNullable<ClientOneQuery>["notes"]>;

const notesFormSchema = object().shape({
  name: string()
    .required("Note is required")
    .max(400, "Note cannot be more than 400 characters"),
});

function NotesList({ notes, onRefetch, clientId, roleType }: NotesListProps) {
  const isAllowWrite = useWritePermission(roleType);
  const { currentEmployee } = useContext(AppContext);
  const theme = useMantineTheme();
  const modals = useModals();
  const { TransitionMenu, setActiveOption } = useTransitionMenu();

  const [] = useCreateNotesMutation();
  const [createNotes, { loading: createLoading }] = useCreateNotesMutation({
    onCompleted: () => {
      onRefetch();
      closeModal(ModalId.AddNote);
    },
  });
  const [updateNotes, { loading: updateLoading }] = useUpdateNotesMutation({
    onCompleted: () => {
      onRefetch();
      closeModal(ModalId.EditNote);
    },
  });
  const [deleteNotes] = useDeleteNotesMutation({
    onCompleted: () => {
      onRefetch();
      closeModal(ModalId.DeleteTransition);
    },
  });

  const handleOpenListMenu = (option: NoteElement) => {
    setActiveOption(option);
  };

  const handleSubmission = (values: string, id?: string) => {
    if (clientId) {
      if (id) {
        updateNotes({
          variables: {
            data: {
              content: values,
              client: {
                id: clientId,
              },
              ...(currentEmployee && {
                author: {
                  id: currentEmployee.id,
                },
              }),
            },
            updateNotesId: id,
          },
        });
        return;
      }
      createNotes({
        variables: {
          data: {
            content: values,
            client: {
              id: clientId,
            },
            ...(currentEmployee && {
              author: {
                id: currentEmployee.id,
              },
            }),
          },
        },
      });
    }
  };

  const onCreate = () => {
    modals.openModal({
      modalId: ModalId.AddNote,
      closeOnClickOutside: false,
      title: "Create Note",
      children: (
        <NameInputForm
          schema={notesFormSchema}
          onSubmit={(values) => {
            handleSubmission(values.name);
          }}
          loading={createLoading}
          onCancel={() => closeModal(ModalId.AddNote)}
          type="Note"
          inputType={InputFormElement.TEXTAREA}
        />
      ),
    });
  };

  const onEdit = ({ id, content }: NoteElement) => {
    modals.openModal({
      modalId: ModalId.EditNote,
      closeOnClickOutside: false,
      title: `Edit Note`,
      children: (
        <NameInputForm
          schema={notesFormSchema}
          onSubmit={(values) => {
            handleSubmission(values.name, id);
          }}
          loading={updateLoading}
          value={content}
          onCancel={() => closeModal(ModalId.EditNote)}
          type="Note"
          inputType={InputFormElement.TEXTAREA}
        />
      ),
    });
  };

  const onDelete = (id: string) => {
    deleteNotes({ variables: { deleteNotesId: id } });
  };
  let color = theme.colors.secondaryTextColor[6];

  return (
    <ActionCard
      title="Notes"
      rightIconButton={
        isAllowWrite ? (
          <ActionIcon
            size="lg"
            onClick={() => onCreate()}
            sx={{
              "&:hover": {
                backgroundColor: theme.colors.primary[4],
              },
            }}
          >
            <IconPlaylistAdd color={theme.colors.primaryTextColor[6]} />
          </ActionIcon>
        ) : null
      }
      height={HeightCard.sm}
      radius={CardRadius.sm}
      size="md"
    >
      {!notes.length ? (
        <Flex justify="center" align="center">
          <Text color={color}>Click</Text>&nbsp;
          {
            <ActionIcon
              disabled={!isAllowWrite}
              size="lg"
              onClick={() => onCreate()}
              sx={{
                "&:hover": {
                  backgroundColor: theme.colors.secondary[4],
                },
              }}
            >
              <IconPlaylistAdd color={theme.colors.primaryTextColor[6]} />
            </ActionIcon>
          }
          &nbsp;
          <Text color={color}>to add a note</Text>
        </Flex>
      ) : (
        <List w="100%" listStyleType="none" style={{ color }}>
          {notes.map((item, index) => {
            const authorName = item.author
              ? `${item.author?.user.firstName}  ${item.author?.user.lastName}`
              : "";
            const noteUpdatedOn = ISOformatedDateTime(item.updatedDate);
            return (
              <List.Item
                w="100%"
                icon={null}
                key={item.id}
                c={color}
                sx={{
                  "&": {
                    position: "relative",
                  },
                  "& ::marker": {
                    content: "none",
                  },
                  "& .mantine-List-itemWrapper": {
                    display: "flex",
                  },
                  "& .mantine-List-itemWrapper > span > div": {
                    alignItems: "center",
                  },
                }}
              >
                {!!index && (
                  <Divider style={{ borderColor: theme.colors.gray[2] }} />
                )}
                <Flex align="center" justify="space-between">
                  <Flex align="center">
                    <Avatar
                      src={item.author?.user.profilePicUrl || null}
                      alt="no image here"
                      size={40}
                    >
                      {generateInitials(authorName)}
                    </Avatar>
                    <Flex align="flex-start" direction={"column"} m={10}>
                      <Text size={13} fw={500}>
                        {item.content as string}
                      </Text>
                      <Text size={11} c="dimmed">
                        {authorName && `${authorName} -`} {noteUpdatedOn}
                      </Text>
                    </Flex>
                  </Flex>
                  {item.isMigrated && (
                    <Badge mx={"xl"} sx={{ minWidth: 90 }}>
                      Migrated
                    </Badge>
                  )}
                  {isAllowWrite && (
                    <TransitionMenu
                      onClick={() => handleOpenListMenu(item)}
                      onEdit={() => onEdit(item)}
                      onDelete={() => onDelete(item.id)}
                      id={item.id}
                    />
                  )}
                </Flex>
              </List.Item>
            );
          })}
        </List>
      )}
    </ActionCard>
  );
}

export default NotesList;
